// @ts-strict-ignore
import React, { FC, useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import classNames from "classnames";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { CustomizationThreediumPlayer } from "./customization-threedium-player";
import { IThreediumError, IThreediumTemplate } from "constants/product-customizations/threedium";
import {
    IThreediumAttribute,
    IThreediumCustomization,
    IThreediumModel,
    threediumModelToTemplate,
} from "constants/product-customizations/customization";

const useStyles = makeStyles(() => ({
    root: {
        height: "100%",
        position: "relative",
    },
    header: {
        height: "40px",
        width: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "transparent",
    },
    customizer: {
        display: "block",
        position: "relative",
        height: "100%",
    },
    threediumContainer: {
        position: "relative",
        transition: "opacity 1000ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
        opacity: 0,
        width: "100%",
        height: "100%",
    },
    visible: {
        opacity: 1,
    },
    loader: {
        position: "absolute",
        top: "0px",
        right: "0px",
        width: "100%",
        height: "100%",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    selectInput: {
        padding: "0px 32px 0px 0px",
    },
}));

export interface ICustomizer {
    title?: string;
    productModel?: IThreediumModel;
    template?: IThreediumTemplate;
    attribute: IThreediumAttribute;
    customizations?: IThreediumCustomization[];
    visualizerId?: string;
    onError?: (error: Error, visualizerId: string) => void;
}

export const Customizer3d: FC<ICustomizer> = ({
    title,
    template,
    productModel,
    attribute,
    customizations,
    visualizerId,
    onError,
}) => {
    const classes = useStyles();
    const [sceneLoading, setSceneLoading] = useState(true);
    const productThreediumTemplate = React.useMemo(() => threediumModelToTemplate(productModel), [productModel]);

    const handleLoadError = useCallback(
        (err: IThreediumError | ErrorEvent, erroredTemplate: IThreediumTemplate) => {
            const errorMessage = (err as IThreediumError).errorMessage ?? (err as ErrorEvent).message;
            onError?.(new Error(`${errorMessage} ${err.error}`.trim()), visualizerId);
        },
        [onError, visualizerId]
    );

    const handleModelLoad = useCallback(() => setSceneLoading(false), []);

    return (
        <div className={classes.root}>
            {title && (
                <div className={classes.header}>
                    <Typography variant="h4">{title}</Typography>
                </div>
            )}
            <div className={classes.customizer}>
                {sceneLoading && (
                    <div className={classes.loader}>
                        <CircularProgress />
                    </div>
                )}
                <div className={classNames(classes.threediumContainer, !sceneLoading && classes.visible)}>
                    <CustomizationThreediumPlayer
                        template={productThreediumTemplate ?? template}
                        attribute={attribute}
                        customizations={customizations}
                        onError={handleLoadError}
                        onLoadError={handleLoadError}
                        onModelLoad={handleModelLoad}
                    />
                </div>
            </div>
        </div>
    );
};
